import { createContext } from 'react';
import { createValueStore, type IReadonlyValueStore } from '@srm/ui/lib/valueStore';
import { IDocumentModalContext } from '@/types';
import type { IDocumentValidationIndex } from '@utils/validation/document';

export const DocumentModalContext = createContext<IDocumentModalContext>({});

/**
 * Индекс зависимостей resolver-валидации документа. Контроллеры используют его,
 * чтобы передать RHF штатный `deps`: после blur исходного поля RHF отдельно
 * применяет ошибки зависимых полей, которые иначе отбрасывает `schemaErrorLookup`.
 */
export const DocumentValidationIndexContext = createContext<IDocumentValidationIndex | null>(null);

/**
 * Когда true, контролы не запускают invokeChangesOnNew/onEdit при маунте:
 * каскады уже выполнил централизованный проход документа.
 * Не-документные формы провайдер не ставят → контролы работают как раньше (per-field).
 */
export const CentralizedSideEffectsContext = createContext<boolean>(false);

/**
 * true после завершения централизованного стартового прохода (runInvokeChangesInit).
 * Только ПОСЛЕ этого per-field контролы (Field/Dict/Abook) выполняют invokeChanges при
 * позднем появлении видимости (см. useBecameVisible): во время самого прохода раскрытие
 * контрола уже обрабатывает централизованный обход по порядку схемы, поэтому дублировать
 * каскад из per-field нельзя (runInvokeChangesInit не идемпотентен).
 * Стором, а не значением: смена признака на каждом входе в правку и выходе из неё перерисовывала бы все поля
 * документа, хотя решение гейта у большинства не меняется. Признак относится к проходу текущего режима: в рендере
 * смены режима он уже false (см. useCentralizedDocumentInit).
 */
export const CentralizedInitCompleteContext = createContext<IReadonlyValueStore<boolean>>(createValueStore(false));

/**
 * Реестр контролов, чей стартовый invokeChanges-каскад уже выполнен (централизованным
 * проходом или per-field при позднем маунте). Нужен контролам, которые в момент раскрытия
 * видимости были размонтированы (скрытый контейнер, ленивая вкладка): они монтируются сразу
 * видимыми, перехода false→true не видят, и только отсутствие ключа в реестре говорит, что
 * их каскад ещё не выполнялся. Живёт одно поколение документа (см. useCentralizedDocumentInit).
 */
export interface ICentralizedInitExecutedKeys {
    has(key: string): boolean;
    add(key: string): void;
}

export const CentralizedInitExecutedKeysContext = createContext<ICentralizedInitExecutedKeys | null>(null);

/**
 * Регистрация императивных валидаторов у resolver-а документа. RHF игнорирует
 * register-level `validate` при наличии resolver, поэтому mount-bound проверки
 * (вложения: `ctl.isValid()`) регистрируются здесь, а прогоняет их сам resolver
 * в конце каждого запуска. `register` возвращает функцию снятия регистрации.
 */
export interface IDocumentImperativeValidators {
    register(path: string, validate: () => boolean | undefined): () => void;
}

export const DocumentImperativeValidatorsContext = createContext<IDocumentImperativeValidators | null>(null);
